import { z } from 'zod';
import { listingValues, listingOptions, yesNo, numberParameter, switchParameter, describeOnce } from './shared.js';

const surfaceIndex = z.number().int().nonnegative();
// PromptingGuide pp141–142; Diagnostic foo.11.11–.14. Omitted controls are
// resolved by CODE V, including symmetry-dependent sampling and clearance.
const footprintCommon = {
  listing: z.enum(listingValues).default('No').describe('LIS: list ray coordinates. Native default No; select Yes for the numerical footprint plot. No never forces ray-coordinate output.'),
  mode: z.enum(['native', 'grid', 'boundary']).default('native').describe('BOU: native chooses grid for obscured/OR-aperture systems and boundary otherwise. CODE V may override boundary with grid.'),
  rays_across_diameter: z.number().nullable().default(null).describe('NRD: approximate ray count across the pupil for grid/clearance tracing. Fractional values are accepted by CODE V. Null retains its native wavelength-dependent sampling.'),
  boundary_angles: z.array(z.strictObject({start: z.number(), end: z.number(), increment: z.number()})).max(1).default([]).describe('AZI: optional boundary search start, end and increment in degrees. An empty list retains CODE V’s symmetry-dependent default.'),
  boundary_resolution: z.number().nullable().default(null).describe('RAR: boundary search resolution as a pupil-radius fraction. PromptingGuide documents 0.001; null leaves CODE V to choose its default.'),
  exclude_obscured: yesNo('OBS: exclude obscured rays from clearance checking. Native default No.'),
  skip_dummy_surfaces: yesNo('SKI: skip dummy surfaces in clearance checking. Native default No.'),
  full_clearance: yesNo('FUL: list all minimum clearances and plot conflict surfaces. Native default No. This can produce clearance tables without a footprint plot.'),
};
const origin = z.array(z.strictObject({x: z.number(), y: z.number()}));
// ORI names its surface, and the batch grammar admits only ORI Sk, so an origin
// needs an explicit surface; the image-surface default takes none.
const footprintSurfaces = [
  {
    surface: z.null().default(null).describe('SUR: one surface index. Null (the default) omits SUR, so CODE V uses its default, the image surface (PromptingGuide p141: Def: SI). A single index and a range have different native limit semantics; this view supports a single index.'),
    origin: origin.max(0, 'Select a surface to set an origin; the image-surface default takes none.').default([]),
  },
  {
    surface: surfaceIndex,
    origin: origin.max(1).default([]).describe('ORI: optional origin in lens units for the selected surface. Empty retains native 0,0; returned coordinates are not shifted.'),
  },
];
const footprintClearances = [
  {
    clearance: z.enum(['native', 'Yes', 'No']).default('native').describe('CLE: native enables clearance checking for decentered systems; Yes or No explicitly selects it.'),
    gap: z.null().default(null),
  },
  {
    clearance: z.literal('gap'),
    gap: z.number().describe('CLE gap_size: native safety margin in lens units; CODE V validates the supplied value.'),
  },
];
const footprintSettings = z.union(describeOnce(footprintSurfaces.flatMap(surface =>
  footprintClearances.map(clearance => ({...footprintCommon, ...surface, ...clearance}))),
).map(shape => z.strictObject(shape)));

export default {
    label: 'Footprint', group: 'system',
    description: 'Ray footprint on a selected surface, with the beam limits.',
    settingsSchema: footprintSettings,
    parameters: [
      numberParameter('surface', 'Surface', {integer: true, blank: 'CODE V default (image surface)'}),
      switchParameter('listing', 'Ray coordinates'),
      {key: 'mode', label: 'Mode', kind: 'select', options: [
        {value: 'native', label: 'CODE V default'}, {value: 'grid', label: 'Grid'}, {value: 'boundary', label: 'Boundary'},
      ]},
      numberParameter('rays_across_diameter', 'Rays across diameter', {blank: 'CODE V default'}),
      {key: 'boundary_angles', label: 'Boundary search angles', kind: 'rows', columns: [
        numberParameter('start', 'Start', {unit: 'degrees'}), numberParameter('end', 'End', {unit: 'degrees'}), numberParameter('increment', 'Increment', {unit: 'degrees'}),
      ]},
      numberParameter('boundary_resolution', 'Boundary resolution', {blank: 'CODE V default (0.001)', unit: 'pupil radius fraction'}),
      {key: 'origin', label: 'Origin', kind: 'rows', columns: [
        numberParameter('x', 'X', {unit: 'lens units'}), numberParameter('y', 'Y', {unit: 'lens units'}),
      ]},
      {key: 'clearance', label: 'Clearance check', kind: 'select', options: [
        {value: 'native', label: 'CODE V default'}, ...listingOptions, {value: 'gap', label: 'Gap threshold'},
      ]},
      numberParameter('gap', 'Gap', {unit: 'lens units', when: {clearance: ['gap']}}),
      switchParameter('exclude_obscured', 'Exclude obscured rays'),
      switchParameter('skip_dummy_surfaces', 'Skip dummy surfaces'),
      switchParameter('full_clearance', 'All clearances'),
    ],
    coverage: 'One surface (the image unless you choose another), one focus position, at the reference wavelength. With Listing set to No, only the summary is shown. For a symmetric lens CODE V can trace half the beam; the missing half is not mirrored. Not supported: surface ranges and wedge apertures.',
  } as const;
